package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.Budget
import com.dotdrives.model.BillingStatus
import com.dotdrives.model.NewBudget
import com.dotdrives.model.NewTag
import com.dotdrives.model.OrgIntegrations
import com.dotdrives.model.Subscription
import com.dotdrives.model.Tag
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.exception.PostgrestRestException
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Clock

class SettingsClient(
    private val client: SupabaseClient = SupabaseProvider.client,
) {
    suspend fun budgets(orgId: String): List<Budget> =
        client.from(BUDGETS_TABLE)
            .select {
                order("period_start", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                }
            }
            .decodeList()

    /** Creates a budget. Revisions use a replacement row to preserve historical drive references. */
    suspend fun addBudget(orgId: String, budget: NewBudget): Budget {
        val id = uuidV7()
        client.from(BUDGETS_TABLE).insert(
            BudgetInsert(
                id = id,
                orgId = orgId,
                scope = budget.scope,
                scopeRef = budget.scopeRef,
                amount = budget.amount,
                periodStart = budget.periodStart,
                periodEnd = budget.periodEnd,
            ),
        )
        return Budget(
            id = id,
            orgId = orgId,
            scope = budget.scope,
            scopeRef = budget.scopeRef,
            amount = budget.amount,
            periodStart = budget.periodStart,
            periodEnd = budget.periodEnd,
            createdAt = Clock.System.now(),
        )
    }

    /** One row per org, read = any member. Null only if the seeding create_org insert hasn't run. */
    suspend fun integrations(orgId: String): OrgIntegrations? =
        client.from(ORG_INTEGRATIONS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                }
            }
            .decodeList<OrgIntegrations>()
            .firstOrNull()

    /**
     * Gated on `has_perm_strict(org_id,'billing.read')` — a `field` caller's request is
     * answered with zero rows by RLS, not an error, so this returns null for that case rather
     * than throwing. Callers must render that as a permission-appropriate state, not a load
     * failure — see docs/schema.md's controller ruling on this module's permission shape.
     */
    suspend fun subscription(orgId: String): Subscription? =
        client.postgrest.rpc("effective_subscription", buildJsonObject { put("p_org", orgId) })
            .decodeList<Subscription>()
            .firstOrNull()

    suspend fun setFiscalPeriod(orgId: String, month: Int, day: Int) {
        client.postgrest.rpc("set_org_fiscal_period", buildJsonObject {
            put("p_org", orgId); put("p_month", month); put("p_day", day)
        })
    }

    suspend fun goalsProgress(orgId: String, archived: Boolean = false): com.dotdrives.model.GoalsProgress =
        client.postgrest.rpc("settings_goals_progress", buildJsonObject {
            put("p_org", orgId); put("p_archived", archived)
        }).decodeAs()

    suspend fun goalProgress(orgId: String, kind: String, goalId: String): Double =
        client.postgrest.rpc("settings_goal_progress", buildJsonObject {
            put("p_org", orgId); put("p_kind", kind); put("p_goal", goalId)
        }).decodeAs<GoalProgressRow>().raised

    /**
     * `public.billing_status(p_org)` — the narrow, security-definer surface every role
     * including `field` may call, returning only `{status, trial_ends_at}`. Powers the
     * trial/past-due banner; never a substitute for [subscription] on the Billing tab itself.
     */
    suspend fun billingStatus(orgId: String): BillingStatus? =
        client.postgrest.rpc(
            function = "billing_status",
            parameters = buildJsonObject {
                put("p_org", orgId)
            },
        ).decodeList<BillingStatus>().firstOrNull()

    /** `tags_read` — any member. Ordered by usage so the most-used tags surface first, matching the autocomplete's own purpose. */
    suspend fun tags(orgId: String): List<Tag> =
        client.from(TAGS_TABLE)
            .select {
                order("usage_count", Order.DESCENDING)
                order("name", Order.ASCENDING)
                filter {
                    eq("archived", false)
                    eq("org_id", orgId)
                }
            }
            .decodeList()

    /** Catalog creation; rename/recolor and archive use tenant-checked RPCs. */
    suspend fun addTag(orgId: String, tag: NewTag): Tag {
        val id = uuidV7()
        try {
            client.from(TAGS_TABLE).insert(
                TagInsert(id = id, orgId = orgId, name = tag.name, color = tag.color),
            )
        } catch (error: PostgrestRestException) {
            val isDuplicateTag = error.code == UNIQUE_VIOLATION && error.message?.contains(TAG_NAME_CONSTRAINT) == true
            if (isDuplicateTag) throw IllegalStateException("A tag named \"${tag.name}\" already exists")
            throw error
        }
        return Tag(id = id, orgId = orgId, name = tag.name, color = tag.color, usageCount = 0, createdAt = Clock.System.now())
    }

    suspend fun updateBudget(orgId: String, id: String, budget: NewBudget): Budget =
        client.postgrest.rpc("revise_settings_budget", buildJsonObject {
            put("p_org", orgId); put("p_id", id); put("p_scope", budget.scope)
            put("p_ref", budget.scopeRef); put("p_amount", budget.amount)
            put("p_start", budget.periodStart); put("p_end", budget.periodEnd)
        }).decodeList<Budget>().single()

    suspend fun archiveBudget(orgId: String, id: String) {
        client.postgrest.rpc("archive_settings_budget", buildJsonObject { put("p_org", orgId); put("p_id", id) })
    }

    suspend fun updateTag(orgId: String, id: String, tag: NewTag): Tag {
        try {
            return client.postgrest.rpc("revise_settings_tag", buildJsonObject {
                put("p_org", orgId); put("p_id", id); put("p_name", tag.name); put("p_color", tag.color)
            }).decodeList<Tag>().single()
        } catch (error: PostgrestRestException) {
            if (error.code == UNIQUE_VIOLATION) throw IllegalStateException("A tag named “${tag.name}” already exists, including archived tags")
            throw error
        }
    }

    suspend fun archiveTag(orgId: String, id: String) {
        client.postgrest.rpc("archive_settings_tag", buildJsonObject { put("p_org", orgId); put("p_id", id) })
    }

    private companion object {
        const val BUDGETS_TABLE = "budgets"
        const val ORG_INTEGRATIONS_TABLE = "org_integrations"
        const val SUBSCRIPTIONS_TABLE = "subscriptions"
        const val TAGS_TABLE = "tags"
        const val UNIQUE_VIOLATION = "23505"
        const val TAG_NAME_CONSTRAINT = "tags_org_id_lower_name_idx"
    }
}

@Serializable
private data class GoalProgressRow(val raised: Double)

@Serializable
private data class BudgetInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val scope: String,
    @SerialName("scope_ref")
    val scopeRef: String?,
    val amount: Double,
    @SerialName("period_start")
    val periodStart: String,
    @SerialName("period_end")
    val periodEnd: String,
)

@Serializable
private data class TagInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val name: String,
    val color: String,
)
